import { useState, type FormEvent } from 'react';
import { CreateBuilding, CreateUnit, UnitKind, type Building, type Unit } from '@syndiqia/registry/contracts';
import { errorMessage } from '../api';
import { useI18n } from '../i18n';
import { useRegistryMutation } from '../queries';
import { Alert, Field, toNumber, validate, type FieldErrors } from './ui';

// Configuration forms (Settings): buildings and units are set up once, at onboarding.

export function BuildingForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [values, setValues] = useState({ name: '', streetAddress: '', city: '', postalCode: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useRegistryMutation<CreateBuilding, Building>(
    (api, base, input) => api(`${base}/buildings`, { method: 'POST', body: input }),
    ['buildings'],
  );

  const set = (key: keyof typeof values) => (e: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(CreateBuilding, values);
    setErrors(result.errors ?? {});
    if (result.data) create.mutate(result.data, { onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <Field label={t.buildings.name} error={errors.name}>
        <input value={values.name} onChange={set('name')} autoFocus />
      </Field>
      <Field label={t.buildings.streetAddress} error={errors.streetAddress}>
        <input value={values.streetAddress} onChange={set('streetAddress')} autoComplete="street-address" />
      </Field>
      <div className="row">
        <Field label={t.buildings.city} error={errors.city}>
          <input value={values.city} onChange={set('city')} autoComplete="address-level2" />
        </Field>
        <Field label={t.buildings.postalCode} hint={t.common.optional} error={errors.postalCode}>
          <input value={values.postalCode} onChange={set('postalCode')} autoComplete="postal-code" placeholder="H2X 1Y4" />
        </Field>
      </div>
      {create.isError && <Alert>{errorMessage(create.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={create.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

export function UnitForm({ buildingId, onDone }: { buildingId: string; onDone: () => void }) {
  const { t } = useI18n();
  const [values, setValues] = useState({ label: '', kind: 'residential', cadastralLot: '', floor: '', areaM2: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useRegistryMutation<CreateUnit, Unit>(
    (api, base, input) => api(`${base}/units`, { method: 'POST', body: input }),
    ['units'],
  );

  const set = (key: keyof typeof values) => (e: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(CreateUnit, {
      buildingId,
      label: values.label,
      kind: values.kind,
      cadastralLot: values.cadastralLot,
      floor: toNumber(values.floor),
      areaM2: toNumber(values.areaM2),
    });
    setErrors(result.errors ?? {});
    if (result.data) {
      create.mutate(result.data, {
        onSuccess: () => {
          // Keep the form open with the same type: units are usually entered in a row.
          setValues((v) => ({ ...v, label: '', cadastralLot: '', areaM2: '' }));
        },
      });
    }
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={t.units.label} error={errors.label}>
          <input value={values.label} onChange={set('label')} autoFocus placeholder="101" />
        </Field>
        <Field label={t.units.kind} error={errors.kind}>
          <select value={values.kind} onChange={set('kind')}>
            {UnitKind.options.map((k) => (
              <option key={k} value={k}>
                {t.units.kinds[k]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="row">
        <Field label={t.units.floor} hint={t.common.optional} error={errors.floor}>
          <input value={values.floor} onChange={set('floor')} inputMode="numeric" />
        </Field>
        <Field label={t.units.area} hint={t.common.optional} error={errors.areaM2}>
          <input value={values.areaM2} onChange={set('areaM2')} inputMode="decimal" />
        </Field>
        <Field label={t.units.cadastralLot} hint={t.common.optional} error={errors.cadastralLot}>
          <input value={values.cadastralLot} onChange={set('cadastralLot')} placeholder="1 234 567" />
        </Field>
      </div>
      {create.isError && <Alert>{errorMessage(create.error, t)}</Alert>}
      {create.isSuccess && !create.isPending && <Alert tone="info">✓ {create.data.label}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={create.isPending}>
          {t.common.add}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
